import { useEffect, useRef, useState, useCallback } from 'react';
import Vapi from '@vapi-ai/web';
import { envConfig } from '../config/vapi.config';
import { useApp } from '../contexts/AppContext';

export interface VapiMessage {
  id: string;
  role: string;
  content: string;
}

export const useVapi = () => {
  const { dispatch } = useApp();
  const vapi = useRef<Vapi | null>(null);
  const [status, setStatus] = useState<'Disconnected' | 'Connected'>('Disconnected');
  const [callActive, setCallActive] = useState(false);
  const [chatMessages, setChatMessages] = useState<VapiMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);

  // Function to update chat messages in the UI
  const updateChat = useCallback((conversation: any[]) => {
    console.log('Updating chat with conversation:', conversation);
    
    // Check if conversation is valid
    if (!conversation || !Array.isArray(conversation)) {
      console.log('Invalid conversation data:', conversation);
      return;
    }
    
    // Map conversation to our message format
    const messages = conversation.map((message, index) => ({
      id: `voice-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: message.role,
      content: message.content || 'Processing request...',
    }));
    
    // Filter out system messages
    const filteredMessages = messages.filter((el) => el.role !== 'system');
    
    console.log('Filtered messages:', filteredMessages);
    console.log('Total conversation items:', conversation.length);
    console.log('Filtered messages count:', filteredMessages.length);
    
    // Update local state with complete array (replace, don't append)
    setChatMessages(filteredMessages);
    
    // Clear existing voicebot messages and add all current messages
    dispatch({ type: 'CLEAR_VOICEBOT_MESSAGES' });
    
    // Add all messages to AppContext for display in chat area
    filteredMessages.forEach((message) => {
      const chatMessage = {
        id: message.id,
        content: message.content,
        sender: message.role === 'user' ? 'user' as const : 'assistant' as const,
        timestamp: new Date(),
        type: 'voice' as const,
      };
      dispatch({ type: 'ADD_VOICEBOT_MESSAGE', payload: chatMessage });
    });
  }, [dispatch]);

  // Initialize Vapi and set up event listeners
  useEffect(() => {
    vapi.current = new Vapi(envConfig.vapi.token);

    // Listener for when a call starts
    vapi.current.on('call-start', () => {
      console.log('Call started');
      setStatus('Connected');
      setCallActive(true);
    });

    // Listener for when a call ends
    vapi.current.on('call-end', () => {
      console.log('Call ended');
      setStatus('Disconnected');
      setCallActive(false);
      setIsProcessing(false);
    });

    // Listener for receiving messages
    vapi.current.on('message', (message: any) => {
      console.log('Vapi message received:', message);
      if (message.type === 'conversation-update') {
        updateChat(message.conversation);
      }
    });

    // Listener for volume level updates
    vapi.current.on('volume-level', (level: number) => {
      setVolumeLevel(level);
    });

    // Listener for error events
    vapi.current.on('error', (error: any) => {
      console.log('Vapi error received:', error);
      setStatus('Disconnected');
      setCallActive(false);
      setIsProcessing(false);
      
      // Handle specific error types
      if (error.error && error.error.type === 'ejected') {
        console.log('Call ended by user or system');
      } else {
        console.error('Vapi error:', error);
      }
    });

    return () => {
      if (vapi.current) {
        vapi.current.stop();
      }
    };
  }, [updateChat]);

  const startCall = () => {
    if (vapi.current && !callActive) {
      setIsProcessing(true);
      // Clear previous voicebot messages when starting new call
      dispatch({ type: 'CLEAR_VOICEBOT_MESSAGES' });
      setChatMessages([]);
      vapi.current.start(envConfig.vapi.assistant_id);
    }
  };

  const stopCall = () => {
    if (vapi.current && callActive) {
      vapi.current.stop();
    }
  };

  const toggleCall = () => {
    if (callActive) {
      stopCall();
    } else {
      startCall();
    }
  };

  return {
    status,
    callActive,
    chatMessages,
    isProcessing,
    volumeLevel,
    startCall,
    stopCall,
    toggleCall,
  };
};
